import React, { useCallback, useEffect, useMemo, useRef, useState, RefObject } from 'react';
import Paginacao from '../../../components/paginacao/Paginacao';
import { useTableColumns } from '../../../hooks/useTableColumns';
import { getUserById, getUsers, User } from '../../../api/usersApi';
import SearchIcon from '../../../img/search-icon.svg';
import FullScreenLoader from '../../../components/loading/FullScreenLoader';
import Register from '../../../components/modal/register/RegisterUserModal';
import UsersMyInfo from '../../../components/modal/users/UsersMyInfo';
import UpdateOtherUserModal from '../../../components/modal/update-other-user/UpdateOtherUserModal';
import { getUserIdFromToken } from '../../user/utils/auth';
import { notify } from '../../../shared/feedback/notify';
import ModalFiltroUsuarios, {
  FiltrosUsuarios,
  FILTROS_USUARIOS_PADRAO,
} from '../../../components/modal/filtro-usuarios/ModalFiltroUsuarios';
import FilterIcon from '../../../img/filter.svg';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import HistoricoAlteracoes from '../../../components/historico-alteracoes/HistoricoAlteracoes';
import '../../../components/como-funciona/help-circle-btn.css';
// Esqueleto de página compartilhado (`recepcao-conte-do`, `recepcao-title`, `recepcao-header-row`,
// `recepcao-search-bar`). Import explícito de propósito: depender de CSS carregado por outra tela
// é acoplamento invisível — foi o que originou o BUG-V74 (at-67).
import '../../recepcao/pages/Recepcao.css';
import './user.css';

interface UsersTableProps {
  sidebarRef?: RefObject<HTMLDivElement>;
}

const headers = [
  'Nome completo',
  'Username',
  'E-mail',
  'Tipo de usuário',
  'Admin',
  'Cód. voluntário',
  'Cód. regional',
  'Status',
];


const UsersTablePage: React.FC<UsersTableProps> = ({ sidebarRef }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [searchInput, setSearchInput] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState<boolean>(false);
  const [isMyInfoModalOpen, setIsMyInfoModalOpen] = useState<boolean>(false);
  const [isUpdateOtherUserModalOpen, setIsUpdateOtherUserModalOpen] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  // at-61: os filtros vivem num modal (molde do Contac) e são aplicados em bloco. O toggle
  // booleano "Mostrar inativos" virou o status tri-estado — ele não sabia expressar
  // "somente inativos", que era o caso pedido.
  const [filtros, setFiltros] = useState<FiltrosUsuarios>(FILTROS_USUARIOS_PADRAO);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
  const [isHelpOpen, setIsHelpOpen] = useState<boolean>(false);
  const [isHistoricoOpen, setIsHistoricoOpen] = useState<boolean>(false);
  const headerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const currentUserId = getUserIdFromToken();
  // Admin é o userType ADMIN — a flag isAdmin é legada e não deve ser usada.
  // O backend pode retornar o userType como enum ou como nome de exibição.
  const isCurrentUserAdmin =
    currentUser?.userType === 'ADMIN' || currentUser?.userType === 'Administrador';
  // TI gerencia usuários (inclusive inativos), mas não pode alterar administradores (at-25).
  const isCurrentUserTi = currentUser?.userType === 'TI';
  const isUserManager = isCurrentUserAdmin || isCurrentUserTi;
  const isAdminTarget = (user: User) =>
    user.userType === 'ADMIN' || user.userType === 'Administrador';

  // Valores renderizados por linha, memoizados para o hook de medição (at-43).
  /**
   * at-81: a célula mostra TODOS os perfis. Fallback no principal para respostas que ainda
   * não devolvem a lista (e para o período entre os deploys de backend e frontend).
   * ⚠️ Uma linha só, com reticências e  — regra do Victor de 12/08/2026.
   */
  const rotuloDosPerfis = (user: User): string =>
    user.papeis && user.papeis.length > 0 ? user.papeis.join(", ") : user.userType || "-";

  const getColumnValues = useCallback(
    (user: User) => [
      user.fullName || '-',
      user.username || '-',
      user.email || '-',
      rotuloDosPerfis(user),
      user.isAdmin ? 'Sim' : 'Não',
      user.codigoVoluntario ?? '-',
      user.codigoRegional ?? '-',
      user.deletedAt ? 'Inativo' : 'Ativo',
    ],
    [],
  );

  const tableRows = useMemo(() => users.map(getColumnValues), [users, getColumnValues]);

  const { tableColumns, tableWidth } = useTableColumns({
    containerRef: tableScrollRef,
    headerCellsRef: headerRefs,
    headers,
    defaultWidths: headers.map(() => 140),
    rows: tableRows,
    cellSelector: '.TabelaContent > .Tabela:not(.TabelaHeader) .Data',
  });

  const totalPages = useMemo(() => {
    const pages = Math.ceil(totalUsers / limit);
    return pages > 0 ? pages : 1;
  }, [totalUsers, limit]);

  const startUserIndex = totalUsers === 0 ? 0 : (currentPage - 1) * limit + 1;
  const endUserIndex = Math.min(currentPage * limit, totalUsers);

  // at-61: os filtros vêm do estado (não por parâmetro) para não repetir a lista crescente de
  // argumentos em cada chamador. As dependências do useCallback garantem recarga a cada mudança.
  const loadUsers = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getUsers({
        page: currentPage,
        limit,
        ...(search.trim() ? { search: search.trim() } : {}),
        status: filtros.status,
        ...(filtros.userType ? { userTypes: filtros.userType } : {}),
        ...(filtros.createdFrom ? { createdFrom: filtros.createdFrom } : {}),
        ...(filtros.createdTo ? { createdTo: filtros.createdTo } : {}),
      });

      setUsers(response.users);
      setTotalUsers(response.total);
    } catch (err: any) {
      notify.errorFrom(err, 'Erro ao carregar os usuários');
    } finally {
      setLoading(false);
    }
  }, [currentPage, limit, search, filtros]);

  /** Aplicar filtro volta para a página 1 — senão a página 3 de um filtro vira lista vazia. */
  const handleApplyFilters = (novos: FiltrosUsuarios) => {
    setFiltros(novos);
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setCurrentPage(1);
  };

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    const loadCurrentUser = async () => {
      if (!currentUserId) return;

      try {
        const response = await getUserById(currentUserId);
        setCurrentUser(response);
      } catch (err: any) {
        setCurrentUser(null);
        notify.errorFrom(err, 'Erro ao carregar os seus dados de usuário');
      }
    };

    loadCurrentUser();
  }, [currentUserId]);

  const handleOpenCreateUserModal = () => {
    setIsCreateUserModalOpen(true);
  };

  const handleCloseCreateUserModal = () => {
    setIsCreateUserModalOpen(false);
    loadUsers();
  };

  const handleOpenMyInfoModal = async () => {
    if (!currentUser && currentUserId) {
      try {
        const response = await getUserById(currentUserId);
        setCurrentUser(response);
      } catch (err: any) {
        // Sem isto o botão "Minhas informações" fica morto sem erro visível (at-38):
        // o modal só renderiza com `currentUser` preenchido, então a falha era dupla-
        // mente silenciosa. Foi assim que o bug do at-26 se manifestou.
        notify.errorFrom(err, 'Erro ao carregar as suas informações');
        return;
      }
    }
    setIsMyInfoModalOpen(true);
  };

  const handleCloseMyInfoModal = () => {
    setIsMyInfoModalOpen(false);
  };

  const handleOpenUpdateOtherUserModal = async (user: User) => {
    try {
      const response = await getUserById(user.id);
      setSelectedUser(response);
      setIsUpdateOtherUserModalOpen(true);
    } catch (err: any) {
      notify.errorFrom(err, 'Erro ao carregar o usuário');
    }
  };

  const handleCloseUpdateOtherUserModal = () => {
    setIsUpdateOtherUserModalOpen(false);
    setSelectedUser(null);
    loadUsers();
  };


  const helpSections: ComoFuncionaSecao[] = [
    {
      titulo: 'O que esta tela controla',
      conteudo: (
        <p>
          O cadastro de todos os usuários do sistema: criação, edição, troca de senha, desativação e reativação.
          Para editar alguém, clique no <strong>nome completo</strong> na lista — não há botão separado.
        </p>
      ),
    },
    {
      titulo: 'Filtros e busca',
      conteudo: (
        <>
          <p>
            O botão <strong>Filtros</strong> abre uma janela com as opções. Elas só passam a valer quando você
            confirma em <strong>Aplicar Filtros</strong> — assim dá para ajustar várias de uma vez, e{' '}
            <strong>Cancelar</strong> descarta o que você mexeu. Os filtros se combinam entre si e com a busca
            por nome ou usuário: o resultado é a interseção de tudo, e o contador acima da tabela sempre
            reflete o total filtrado.
          </p>
          <p>
            <strong>Status</strong> mostra somente ativos (padrão), somente inativos ou os dois juntos. Usuário
            inativo é o que foi desativado: ele continua cadastrado e pode ser reativado, mas não acessa o
            sistema. <strong>Tipo de usuário</strong> filtra por perfil de acesso. <strong>Criado de</strong> e{' '}
            <strong>Criado até</strong> filtram pela data em que o cadastro foi feito, incluindo os dois dias
            informados.
          </p>
          <p>
            Para conferir ou desfazer o que está aplicado, abra o <strong>Filtros</strong> de novo: ele sempre
            mostra os valores em vigor, e o botão <strong>Limpar filtros</strong> ali dentro devolve tudo ao
            estado inicial. Aplicar um filtro sempre volta a lista para a primeira página.
          </p>
        </>
      ),
    },
    {
      titulo: 'Tipo de usuário define o acesso',
      conteudo: (
        <p>
          O campo <strong>Setor</strong> (na criação) e <strong>Tipo de Usuário</strong> (na edição) determinam o
          que a pessoa vê e faz no sistema — cada tela é liberada por perfil. A coluna <strong>Admin</strong> é um
          registro antigo mantido apenas por histórico: quem é administrador hoje é definido pelo tipo{' '}
          <strong>ADMIN</strong>, e o "Sim/Não" dessa coluna não concede poder algum.
        </p>
      ),
    },
    {
      titulo: 'Histórico de alterações',
      conteudo: (
        <p>
          O botão <strong>Histórico de alterações</strong> mostra tudo o que já foi feito nos cadastros:
          criação, edição campo a campo (com o valor anterior e o novo), desativação e reativação, sempre com o
          nome de quem fez e a data. Troca de senha aparece como evento, mas o <strong>valor</strong> da senha
          nunca é guardado nem exibido — nem para o administrador. É o registro para conferir quem mudou o
          perfil de acesso de alguém, e quando.
        </p>
      ),
    },
    {
      titulo: 'Senhas',
      conteudo: (
        <p>
          Ao trocar a senha de <strong>outra pessoa</strong> por esta tela, ela vira provisória: no próximo acesso
          o sistema pedirá que o dono escolha a própria senha (o aviso pode ser adiado). Colaboradores precisam
          apenas de senha com 8 caracteres ou mais; os demais perfis exigem também maiúscula, minúscula, número e
          caractere especial. Promover um colaborador a outro perfil sem trocar a senha no mesmo salvamento também
          marca a senha como provisória.
        </p>
      ),
    },
    {
      titulo: 'Desativar um usuário',
      conteudo: (
        <p>
          A desativação é reversível (o usuário some do acesso, mas o cadastro fica e pode ser reativado) e tem
          efeitos imediatos na oficina: as <strong>atividades em andamento</strong> do colaborador são encerradas
          (as horas vão para a aprovação do Chefe de Oficina) e ele é <strong>desvinculado dos veículos</strong> em
          que trabalhava. Ninguém desativa a si mesmo — o campo fica bloqueado no próprio cadastro.
        </p>
      ),
    },
    {
      titulo: 'Códigos do Contac',
      conteudo: (
        <p>
          <strong>Cód. voluntário</strong> e <strong>Cód. regional</strong> ligam o usuário aos dados financeiros do
          módulo Contac: são eles que fazem um consultor ver somente a própria carteira e uma regional ver somente
          a sua região. Preencha apenas para os perfis que usam o Contac — código errado mostra os dados de outra
          pessoa ou tela vazia.
        </p>
      ),
    },
    ...(isCurrentUserTi
      ? [
          {
            titulo: 'Seu acesso (TI)',
            conteudo: (
              <p>
                O perfil de TI gerencia usuários, <strong>exceto administradores</strong>: usuários do tipo ADMIN
                aparecem na lista sem link de edição, e a opção ADMIN não existe nos seus formulários — criar,
                promover ou alterar administradores é exclusivo de outros administradores. É por isso que alguns
                nomes da lista não são clicáveis para você.
              </p>
            ),
          },
        ]
      : []),
  ];

  return (
    <div
      className="Frame1000004012"
      style={{
        height: loading ? '100vh' : 'fit-content',
        width: '100%',
        display: 'flex',
        justifyContent: loading ? 'center' : 'space-between',
        alignItems: loading ? 'center' : 'flex-start',
        backgroundColor: loading ? '#808080' : 'transparent',
      }}
    >
      {loading && <FullScreenLoader />}

      {!loading && (
        /*
          Esqueleto COMPARTILHADO de página (13/08/2026 — pedido do Victor: o título ficava
          posicionado diferente das demais telas). `recepcao-conte-do`, `recepcao-header-row` e
          `recepcao-title` substituem os paddings e o cabeçalho próprios desta tela, que
          replicavam os mesmos valores à mão — e trazem junto a responsividade completa do
          Recepcao.css (degrau de 1024px, além do de 768px que existia aqui).
        */
        <div className="ConteDo recepcao-conte-do">
          <div className="recepcao-header-row">
            <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Usuários
              <button
                type="button"
                className="help-circle-btn"
                title="Como Funciona"
                onClick={() => setIsHelpOpen(true)}
              >
                ?
              </button>
            </div>
            {/* Mesma busca de antes: as classes compartilhadas trazem exatamente os valores que
                estavam inline aqui (fundo, raio, sombra, altura), então nada muda de aparência. */}
            <div className="recepcao-search-bar">
              <div className="ItemSearch" onClick={handleSearch} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <img src={SearchIcon} alt="Buscar" width={20} height={20} />
              </div>
              <input
                type="text"
                placeholder="Buscar por nome ou usuário..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                className="recepcao-search-input"
              />
            </div>
          </div>

          <div className="VisualizarOrdenar">
            <div
              className="Frame1000004037"
              style={{
                alignSelf: 'stretch',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 12,
                display: 'flex',
              }}
            >
              <div className="CtaBig">
                <div className="Frame1000003987">
                  <div className="ComponentName">Itens por página:</div>
                  <select
                    id="limit"
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="ComponentNameImgIcon"
                    style={{ cursor: 'pointer' }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>

              {/* at-61: na mesma linha do "Itens por página", como no Contac (Filtros e
                  Relatórios lado a lado). O modal é aberto por aqui. */}
              <div className="CtaBig MouseHover" onClick={() => setIsFilterModalOpen(true)}>
                <div className="Frame1000003987">
                  <img src={FilterIcon} alt="Filtros" width="19.80" height="19.80" style={{ cursor: 'pointer' }} />
                  <div className="ComponentName">Filtros</div>
                </div>
              </div>

              {/* at-69 (BUG-V60): a auditoria de usuários era gravada desde sempre e não
                  tinha nenhuma tela que a lesse. Esta é a porta. */}
              <div className="CtaBig MouseHover" onClick={() => setIsHistoricoOpen(true)}>
                <div className="Frame1000003987">
                  <div className="ComponentName">Histórico de alterações</div>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div onClick={handleOpenMyInfoModal} className="CtaSmall" style={{ marginRight: 10 }}>
                <div className="ComponentName">Minhas informações</div>
              </div>
              <div onClick={handleOpenCreateUserModal} className="CtaSmall">
                <div className="ComponentName">+ Criar usuário</div>
              </div>
            </div>
          </div>

          <div className="QuantidadeDeCarros">
            <div
              className="Frame1000004037"
              style={{
                alignSelf: 'stretch',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 12,
                display: 'flex',
              }}
            >
              <div className="CtaQuantidadeDeCarros">
                <div className="Frame1000003987">
                  <div className="ComponentName">
                    {totalUsers === 0
                      ? '0 usuários'
                      : `${startUserIndex}-${endUserIndex} de ${totalUsers} usuários`}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              width: '100%',
              paddingTop: 30,
              background: 'var(--tema-superficie, white)',
              borderRadius: 16,
              border: '1px var(--tema-linha-2, #CACACA) solid',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              display: 'flex',
            }}
          >
            <div className="TabelaScroll" ref={tableScrollRef}>
              <div
                className="TabelaContent"
                style={{
                  ['--table-columns' as any]: tableColumns,
                  width: tableWidth,
                  minWidth: tableWidth,
                }}
              >
                <div className="Tabela TabelaHeader">
                  <div className="Placa">
                    <div className="TTulo">
                      {headers.map((header, index) => (
                        <div
                          key={header}
                          className="Opcoes"
                          ref={(el) => {
                            headerRefs.current[index] = el;
                          }}
                        >
                          {header}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {users.length === 0 ? (
                  <div className="Tabela">
                    <div className="Item">
                      <div className="Data">Nenhum usuário cadastrado.</div>
                    </div>
                  </div>
                ) : (
                  users.map((user) => (
                    <div key={user.id} className="Tabela">
                      <div className="Item">
                        {isCurrentUserTi && isAdminTarget(user) ? (
                          // TI não pode alterar administradores — linha sem link (at-25)
                          <div className="Data">{user.fullName || '-'}</div>
                        ) : (
                          <div
                            className="data-link"
                            onClick={() => handleOpenUpdateOtherUserModal(user)}
                            style={{ cursor: 'pointer' }}
                          >
                            {user.fullName || '-'}
                          </div>
                        )}
                      </div>

                      <div className="Item">
                        <div className="Data">{user.username || '-'}</div>
                      </div>

                      <div className="Item">
                        <div className="Data">{user.email || '-'}</div>
                      </div>

                      <div className="Item">
                        <div className="Data" title={rotuloDosPerfis(user)}>
                          {rotuloDosPerfis(user)}
                        </div>
                      </div>

                      <div className="Item">
                        <div className="Data">{user.isAdmin ? 'Sim' : 'Não'}</div>
                      </div>

                      <div className="Item">
                        <div className="Data">{user.codigoVoluntario || '-'}</div>
                      </div>

                      <div className="Item">
                        <div className="Data">{user.codigoRegional || '-'}</div>
                      </div>



                      <div className="Item">
                        <div className="Data">{user.deletedAt ? 'Inativo' : 'Ativo'}</div>
                      </div>

                      {/* Coluna Ações removida - funcionalidade migrada para o modal de edição */}
                    </div>
                  ))
                )}
              </div>
            </div>

            <Paginacao
              paginaAtual={currentPage}
              totalPaginas={totalPages}
              onMudarPagina={setCurrentPage}
            />
          </div>
        </div>
      )}
      <Register
        isOpen={isCreateUserModalOpen}
        onCancel={handleCloseCreateUserModal}
        sidebarRef={sidebarRef}
        isSidebarOpen={false}
      />
      {currentUser && (
        <UsersMyInfo
          isOpen={isMyInfoModalOpen}
          onCancel={handleCloseMyInfoModal}
          user={currentUser}
          setUser={setCurrentUser}
          sidebarRef={sidebarRef}
          isSidebarOpen={false}
        />
      )}
      {selectedUser && (
        <UpdateOtherUserModal
          isOpen={isUpdateOtherUserModalOpen}
          onCancel={handleCloseUpdateOtherUserModal}
          user={selectedUser}
          setUser={setSelectedUser}
          sidebarRef={sidebarRef}
          isSidebarOpen={false}
        />
      )}

      {/* Montagem condicional (não prop `isOpen`): garante que o modal abra sempre com os
          filtros em vigor, sem depender de efeito de reset. Ver o comentário no componente. */}
      {isFilterModalOpen && (
        <ModalFiltroUsuarios
          onClose={() => setIsFilterModalOpen(false)}
          filtrosAtuais={filtros}
          onApplyFilters={handleApplyFilters}
          podeFiltrarStatus={isUserManager}
        />
      )}

      <HistoricoAlteracoes
        isOpen={isHistoricoOpen}
        onClose={() => setIsHistoricoOpen(false)}
        entity="user"
        titulo="Histórico de alterações de usuários"
        descricao={
          'Toda criação, edição, desativação e reativação de usuário fica registrada aqui, com quem fez e quando. ' +
          'Troca de senha aparece como evento, mas o valor da senha nunca é guardado nem exibido.'
        }
        rotuloAlvo="Usuário"
      />

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona — Usuários"
        secoes={helpSections}
      />
    </div>
  );
};

export default UsersTablePage;
